// ── Chart primitives — hand-rolled SVG, no chart library ────────────────────
//
// House rule (ClickstreamPanel, TraceView): this codebase draws its own marks.
// These are the shared primitives so the founder analytics page doesn't grow a
// fifth private copy of "bars scaled by max".
//
// Design rules encoded here, so callers get them for free:
//   · thin marks, 4px rounded data-ends, 2px lines, ≥8px hover targets
//   · a 2px surface-coloured gap between adjacent/stacked fills, so segments
//     read as separate marks rather than one blurred band
//   · recessive grid and axes — the data is the loudest thing on the canvas
//   · text always wears text tokens, never the series colour; a coloured mark
//     sits beside the label to carry identity
//   · every plot has a hover layer. A chart in a browser is interactive; a
//     static one is a screenshot that forgot to be useful
//   · ≥2 series always get a legend, so identity is never colour-alone
//
// COLOUR. The categorical ramp below is fixed and ordered — assigned by slot,
// never cycled, never re-assigned when a filter changes the series count (a
// series keeps its hue so the eye can follow it across a re-render). It is
// validated against this app's dark card surface (#111118): lightness band,
// chroma floor, adjacent-pair CVD separation (worst ΔE 8.4 protan), normal
// vision separation (worst ΔE 19.3) and ≥3:1 contrast all pass.
//
// It deliberately does NOT use accent-*/forge-*: those are the white-label
// hook (AccentInjector re-themes them per org), so a series painted with them
// would silently change hue — and collide with a neighbour — on a client's
// branded portal. Single-series charts DO use accent, because there is no
// identity to confuse: one mark, named by its own title.

import { useState, useId } from 'react';
import { cn } from '../lib/cn';

/** Fixed categorical order. Slot N is always hue N. Never cycle; fold the tail into "Other". */
export const SERIES_HUES = [
  '#3987e5', // 1 blue
  '#d95926', // 2 orange
  '#199e70', // 3 aqua
  '#c98500', // 4 yellow
  '#d55181', // 5 magenta
  '#008300', // 6 green
] as const;

/** The 9th series is never a generated hue — the tail folds into this. */
export const OTHER_HUE = '#6b6b78';

/**
 * Sequential ramp (one hue) for magnitude — heatmap cells. Low → high.
 *
 * Ordered DIM→BRIGHT because this app's chart surface is dark (#111118). The
 * familiar "light means less" convention is a light-background convention: it
 * works on paper because pale ink is closer to a white page. Ported unchanged
 * onto a dark canvas it inverts the meaning — the palest step becomes the
 * LOUDEST mark on the page, so the quiet hours glare and the busy ones sink
 * into the background. What must stay constant is that low values sit near the
 * surface and high values are furthest from it; on dark, that means dim→bright.
 */
const SEQUENTIAL = ['#153a63', '#1b4d85', '#215fa6', '#2a78d6', '#5598e7', '#86b6ef', '#cde2fb'];

/** The card surface these marks sit on — used for the 2px separating gaps. */
const SURFACE = '#111118';

export function seriesHue(index: number): string {
  return SERIES_HUES[index] ?? OTHER_HUE;
}

// ── Stat tile ───────────────────────────────────────────────────────────────
// Sometimes the right chart is not a chart. A single number with a label beats
// a one-bar bar chart every time.

export function StatTile({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-surface-900 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {hint && <p className="mt-0.5 text-[10px] text-gray-600">{hint}</p>}
    </div>
  );
}

// ── Legend ──────────────────────────────────────────────────────────────────

export function Legend({ items }: { items: Array<{ label: string; color: string }> }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
      {items.map(item => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ background: item.color }}
            aria-hidden="true"
          />
          {/* Text wears a text token, never the series colour. */}
          <span className="text-[11px] text-gray-400">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Line / area chart — change over time, ONE series ─────────────────────────
//
// One series per chart on purpose. Two measures on different scales (arrivals
// vs events, say) get two charts stacked, never two y-axes on one canvas: a
// dual axis lets the author choose where the lines cross, which is editorial,
// not analytical.

export interface LinePoint {
  label: string;
  value: number;
}

export function LineChart({
  points,
  color = 'var(--accent-500)',
  height = 132,
  valueSuffix = '',
}: {
  points: LinePoint[];
  color?: string;
  height?: number;
  valueSuffix?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const gradientId = useId();

  if (points.length === 0) {
    return <p className="py-8 text-center text-xs text-gray-600">No data in this window.</p>;
  }

  const w = 720;
  const padX = 8;
  const padY = 12;
  const max = Math.max(1, ...points.map(p => p.value));

  const xAt = (i: number) =>
    points.length === 1 ? w / 2 : padX + (i / (points.length - 1)) * (w - padX * 2);
  const yAt = (v: number) => height - padY - (v / max) * (height - padY * 2);

  const line = points.map((p, i) => `${xAt(i)},${yAt(p.value)}`).join(' ');
  const area = `${padX},${height - padY} ${line} ${xAt(points.length - 1)},${height - padY}`;

  const active = hover === null ? null : points[hover];

  return (
    <div className="relative">
      {/* preserveAspectRatio="none" so the plot FILLS the card at any width.
          The default (xMidYMid meet) letterboxes a wide viewBox inside a
          narrower card — the chart shrinks and floats in the middle with dead
          space either side, which reads as a rendering fault. Stretching the
          x-axis is harmless for a time series (the axis is ordinal), and
          vector-effect below keeps strokes at their true weight rather than
          scaling them with the box. */}
      <svg
        viewBox={`0 0 ${w} ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
        role="img"
        aria-label={`Trend across ${points.length} days, peak ${max}${valueSuffix}`}
        onMouseLeave={() => setHover(null)}
      >
        {/* Recessive baseline — present for reference, never competing.
            vector-effect keeps every stroke at its true weight despite the
            non-uniform scale above; without it a horizontal squeeze thins the
            verticals and a stretch fattens them. */}
        <line
          x1={padX} y1={height - padY} x2={w - padX} y2={height - padY}
          stroke="currentColor" strokeWidth={1} vectorEffect="non-scaling-stroke"
          className="text-white/[0.08]"
        />

        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.22} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>

        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line} fill="none" stroke={color} strokeWidth={2}
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"
        />

        {/* Crosshair for the hovered day. The MARKER is deliberately not an
            SVG <circle>: under preserveAspectRatio="none" a circle scales into
            an ellipse. It is drawn as an HTML dot over the plot instead, where
            it stays round at any card width. */}
        {hover !== null && (
          <line
            x1={xAt(hover)} y1={padY} x2={xAt(hover)} y2={height - padY}
            stroke="currentColor" strokeWidth={1} vectorEffect="non-scaling-stroke"
            className="text-white/20"
          />
        )}

        {/* Invisible hit bands — hover targets are far bigger than the marks. */}
        {points.map((p, i) => {
          const bandW = (w - padX * 2) / points.length;
          return (
            <rect
              key={p.label}
              x={padX + i * bandW} y={0} width={bandW} height={height}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          );
        })}
      </svg>

      {/* Round marker, immune to the SVG's non-uniform scale. 2px surface ring
          so it stays legible where it sits on top of the filled area. */}
      {hover !== null && (
        <span
          className="pointer-events-none absolute h-[9px] w-[9px] rounded-full"
          style={{
            left: `${(xAt(hover) / w) * 100}%`,
            top: yAt(points[hover].value),
            transform: 'translate(-50%, -50%)',
            background: color,
            boxShadow: `0 0 0 2px ${SURFACE}`,
          }}
          aria-hidden="true"
        />
      )}

      <div className="mt-1 flex justify-between text-[10px] text-gray-600">
        <span>{points[0].label}</span>
        <span>{points[points.length - 1].label}</span>
      </div>

      <Tooltip visible={active !== null}>
        {active && (
          <>
            <span className="text-gray-400">{active.label}</span>
            <span className="ml-2 font-mono font-semibold text-white">
              {active.value.toLocaleString()}{valueSuffix}
            </span>
          </>
        )}
      </Tooltip>
    </div>
  );
}

// ── Stacked bars — composition over time ────────────────────────────────────

export interface StackedDay {
  label: string;
  segments: Array<{ key: string; value: number; color: string }>;
}

export function StackedBars({ days, height = 148 }: { days: StackedDay[]; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);

  if (days.length === 0) {
    return <p className="py-8 text-center text-xs text-gray-600">No activity in this window.</p>;
  }

  const totals = days.map(d => d.segments.reduce((sum, s) => sum + s.value, 0));
  const max = Math.max(1, ...totals);
  const active = hover === null ? null : days[hover];

  return (
    <div className="relative">
      <div className="flex items-end gap-[3px]" style={{ height }}>
        {days.map((day, i) => {
          const total = totals[i];
          const colHeight = total > 0 ? Math.max(3, (total / max) * height) : 1;
          return (
            <div
              key={day.label}
              className="flex h-full flex-1 cursor-default flex-col justify-end"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              title={`${day.label} — ${total.toLocaleString()} events`}
            >
              {total === 0 ? (
                <div className="w-full rounded-sm bg-white/[0.05]" style={{ height: 1 }} />
              ) : (
                <div
                  className={cn(
                    'flex w-full flex-col-reverse overflow-hidden rounded-t-[4px] transition-opacity',
                    hover !== null && hover !== i && 'opacity-40',
                  )}
                  style={{ height: colHeight }}
                >
                  {day.segments.filter(s => s.value > 0).map(seg => (
                    <div
                      key={seg.key}
                      style={{
                        height: `${(seg.value / total) * 100}%`,
                        background: seg.color,
                        // 2px surface gap so neighbouring segments read as two
                        // marks, not one gradient.
                        boxShadow: `inset 0 -2px 0 0 ${SURFACE}`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-1 flex justify-between text-[10px] text-gray-600">
        <span>{days[0].label}</span>
        <span>{days[days.length - 1].label}</span>
      </div>

      <Tooltip visible={active !== null}>
        {active && (
          <div>
            <p className="mb-1 text-gray-400">{active.label}</p>
            {active.segments.filter(s => s.value > 0).slice(0, 6).map(seg => (
              <p key={seg.key} className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: seg.color }} />
                <span className="text-gray-400">{seg.key}</span>
                <span className="ml-auto pl-3 font-mono text-white">{seg.value.toLocaleString()}</span>
              </p>
            ))}
          </div>
        )}
      </Tooltip>
    </div>
  );
}

// ── Heatmap — magnitude across two dimensions (day × hour) ──────────────────
//
// Sequential: ONE hue, light→dark. Never a rainbow — a multi-hue scale implies
// categories where there is only "more" and "less".

export function Heatmap({
  rows,
  colLabels,
  rowLabel,
}: {
  rows: Array<{ label: string; values: number[] }>;
  colLabels: string[];
  rowLabel: (r: { label: string }) => string;
}) {
  const [hover, setHover] = useState<{ r: number; c: number } | null>(null);
  const max = Math.max(1, ...rows.flatMap(r => r.values));

  const stepFor = (v: number) => {
    if (v === 0) return null; // empty cells stay surface — "nothing" is not "a little"
    const idx = Math.min(SEQUENTIAL.length - 1, Math.floor((v / max) * SEQUENTIAL.length));
    return SEQUENTIAL[idx];
  };

  const active = hover ? rows[hover.r] : null;

  return (
    <div className="relative overflow-x-auto">
      {/* space-y keeps the weekday rows readable as rows — without it the
          columns fuse into single vertical bars and the day axis disappears. */}
      <div className="min-w-[560px] space-y-[2px]">
        {rows.map((row, r) => (
          <div key={row.label} className="flex items-center gap-1.5">
            <span className="w-16 shrink-0 text-right font-mono text-[10px] text-gray-600">
              {rowLabel(row)}
            </span>
            <div className="flex flex-1 gap-[2px]">
              {row.values.map((v, c) => {
                const fill = stepFor(v);
                return (
                  <div
                    key={c}
                    className="h-4 flex-1 rounded-[2px]"
                    // An empty cell needs to read as "nothing happened here",
                    // not as a hole in the grid — too faint and a genuine zero
                    // looks like a rendering gap.
                    style={{ background: fill ?? 'rgba(255,255,255,0.07)' }}
                    onMouseEnter={() => setHover({ r, c })}
                    onMouseLeave={() => setHover(null)}
                    title={`${row.label} ${colLabels[c]} — ${v}`}
                  />
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="w-16 shrink-0" />
          <div className="flex flex-1 justify-between text-[10px] text-gray-600">
            <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
          </div>
        </div>
      </div>

      <Tooltip visible={hover !== null}>
        {hover && active && (
          <>
            <span className="text-gray-400">{active.label} · {colLabels[hover.c]}</span>
            <span className="ml-2 font-mono font-semibold text-white">
              {active.values[hover.c].toLocaleString()}
            </span>
          </>
        )}
      </Tooltip>
    </div>
  );
}

// ── Horizontal magnitude bars — ranked comparison ───────────────────────────

export function RankedBars({
  items,
  color = 'var(--accent-500)',
}: {
  items: Array<{ label: string; value: number; muted?: boolean }>;
  color?: string;
}) {
  if (items.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-600">Nothing to rank yet.</p>;
  }
  const max = Math.max(1, ...items.map(i => i.value));

  return (
    <div className="space-y-1.5">
      {items.map(item => (
        <div key={item.label} className="flex items-center gap-2">
          <span
            className={cn(
              'w-32 shrink-0 truncate text-[11px]',
              item.muted ? 'italic text-gray-600' : 'text-gray-400',
            )}
            title={item.label}
          >
            {item.label}
          </span>
          <div className="h-2 flex-1 rounded-full bg-white/[0.04]">
            <div
              className="h-2 rounded-full"
              // ≥4px so a near-zero value still reads as a mark rather than
              // vanishing into the track.
              style={{ width: `${Math.max(4, (item.value / max) * 100)}%`, background: item.muted ? OTHER_HUE : color }}
            />
          </div>
          <span className="w-12 shrink-0 text-right font-mono text-[11px] text-gray-400">
            {item.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Shared tooltip shell ────────────────────────────────────────────────────
// Reserves its own row rather than floating over the plot: a tooltip that
// covers the marks it describes makes you move the mouse to read the chart.

function Tooltip({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mt-2 min-h-[1.5rem] rounded-lg border border-white/[0.06] bg-surface-800 px-2.5 py-1.5 text-[11px] transition-opacity',
        visible ? 'opacity-100' : 'opacity-0',
      )}
      aria-live="polite"
    >
      {children ?? <span>&nbsp;</span>}
    </div>
  );
}
